import { coreApi, mercuryApi } from '$api'
import type { IPackageModel } from '@loadedhub/core-api-sdk'
import { singletonQueryClient } from '$utils'
import { PackageDetail } from 'src/components/routes/time/_common/interfaces'

enum QueryKeys {
  PayAdjustmentPackages = 'pay-adjustment-packages',
  PayAdjustmentPackage = 'pay-adjustment-package',
}

// NOTE: If you make any changes to any of the endpoint methods please update them to use the new coreApi
// (generated client) endpoints. The were we using legacy mercuryApi as a way to get around a a bug which
// has now been fixed. The new coreApi endpoints are more type safe and will be the standard going forward.

/**
 * Get Pay Adjustment Packages list
 * @param includeActivatedPackagesOnly - when true only returns packages activated for the venue
 * @returns Promise<PackageDetail[]>
 */
export const getAll = async (
  includeActivatedPackagesOnly: boolean = true
): Promise<PackageDetail[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.PayAdjustmentPackages, includeActivatedPackagesOnly],
    queryFn: async () => {
      return await mercuryApi.get<PackageDetail[]>(
        `/pay-rules/packages?includeActivatedPackagesOnly=${includeActivatedPackagesOnly}`
      )
    },
  })
}

/**
 * Drops the cached package lists so the next getAll reflects server truth (call after mutations
 * made outside this service, e.g. package activations).
 */
export const invalidateCache = async (): Promise<void> => {
  await singletonQueryClient.invalidateQueries({
    queryKey: [QueryKeys.PayAdjustmentPackages],
  })
}

/**
 * Get Pay Adjustment Package by Id
 * @param packageId - pay adjustment package id to get
 * @returns Promise<PackageDetail>
 */
export const getById = async (packageId: string): Promise<PackageDetail> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.PayAdjustmentPackage, packageId],
    queryFn: async () => {
      return await mercuryApi.get<PackageDetail>(
        `/pay-rules/packages/${packageId}`
      )
    },
  })
}

/**
 * Promotes a venue's own (UserDefined) package into a system-wide Managed one.
 * The request carries only the id - promotion is its own endpoint precisely because type is not
 * writable through an update, so no metadata travels with it.
 * One-way: there is no endpoint to demote a Managed package back to custom.
 * @param packageId - pay adjustment package to promote
 * @returns the promoted package as the generated SDK model
 */
export const promoteToManaged = async (
  packageId: string
): Promise<IPackageModel> => {
  const promotedPackage = await coreApi.promotePackageToManaged(packageId)

  // The promoted package now belongs to every venue, so any cached list is stale.
  await invalidateCache()

  return promotedPackage
}
